import { plainText } from "../../atoms/text/text";
import type { Membership } from "./channel-lineup.types";

/* No "use client" here, the same reason tooltip.shared.ts carries none: a Server Component
   importing a value from a client module gets an opaque client reference rather than the object. */

/** Every membership the grid draws, in the order the legend lists them (GS9). */
export const MEMBERSHIPS: readonly Membership[] = ["included", "addon", "promo"];

/** One glyph per membership — ✓ included, $ add-on, ★ promo (GR2). */
export interface IndicatorGlyph {
  /** `d` of a filled path, or `undefined` when the glyph is stroked. */
  fill?: string;
  /** `d` values drawn as strokes, for shapes a fill path states badly. */
  strokes?: string[];
}

export const INDICATOR_GLYPH: Record<Membership, IndicatorGlyph> = {
  /* The frame's `check` icon (`14594:2864`), scaled from its 36 box to 16. */
  included: {
    fill:
      "M12.77 4.67 7.86 12.43C7.68 12.72 7.38 12.89 7.05 12.89 6.76 12.89 6.49 12.76 6.3 12.52L3.27 8.8C3.03 8.5 3.06 8.06 " +
      "3.35 7.8 3.62 7.55 4.09 7.54 4.36 7.8L6.93 10.06 11.53 3.85C11.76 3.54 12.23 3.46 12.56 3.67 12.89 3.89 12.99 4.33 12.78 4.67Z"
  },
  addon: {
    strokes: [
      "M8 2.6V13.4",
      "M10.7 5.6C10.7 4.4 9.5 3.7 8 3.7 6.5 3.7 5.3 4.4 5.3 5.7c0 2.7 5.4 1.8 5.4 4.6 0 1.3-1.2 2-2.7 2-1.5 0-2.7-.7-2.7-2"
    ]
  },
  promo: {
    fill:
      "M8 1.8 9.53 5.9 13.9 6.08 10.47 8.8 11.64 13.02 8 10.6 " +
      "4.36 13.02 5.53 8.8 2.1 6.08 6.47 5.9Z"
  }
};

/** AX3 — what a screen reader calls each cell state; `none` is a cell with no membership. */
export type IndicatorStateLabels = Partial<Record<Membership | "none", string>>;

/** GR3 — the tooltip reads "{prefix} {package name}". */
export type IndicatorTooltipPrefixes = Partial<Record<Membership, string>>;

/** AX3's cell name — "A&E, Entertainment: Included". With no state wording authored the name stops
 *  at the package. */
export function indicatorAccessibleName(
  channelName: string,
  packageLabel: string,
  membership: Membership | null,
  stateLabels: IndicatorStateLabels = {}
): string {
  const state = plainText((stateLabels[membership ?? "none"] ?? "").trim());
  const name = `${plainText(channelName)}, ${plainText(packageLabel)}`;
  return state === "" ? name : `${name}: ${state}`;
}

/** The tooltip's wording on the trigger root. `""` means "the label itself". Here rather than in
 *  `indicator.tsx` so `tooltip-scope.client.tsx` can read it without pulling that module across. */
export const TOOLTIP_ATTRIBUTE = "data-cl-tip";
